import React, { Fragment, useEffect, useState } from "react";
import {
  Share,
  SquarePen,
  Trash,
  Menu,
  User,
  LogOut,
  BellPlus,
  Check,
  Clock,
  AlertCircle,
  X,
  Brain,
  Mail,
  Building2,
  Mic
} from "lucide-react";
import { Message } from "../Chat/ChatWindow";
import ModelListBox from "../Model/ModelListBox";
import { Listbox, Transition } from "@headlessui/react";
import { useAuth as cognitoUseAuth } from "react-oidc-context";
import { toast } from "sonner";
import { FileItem } from "../../types";
import {
  addFileToKnowledgeBase,
  FILE_STORAGE_KEY,
  getFilesFromStorage,
  removeFileFromKnowledgeBase,
} from "../../helpers/knowledgeBaseLocalStorage";
import {
  getMeetingNotifications,
  removeMeetingNotification,
  clearAllMeetingNotifications,
  MeetingNotificationItem,
} from "../../helpers/meetingNotificationLocalStorage";
import { getFile, toggleLongTermMemory, toggleShortTermMemory, generateFeedbackSummary } from "../../service/apiService";
import { useAuth } from "../../context/AuthContext";
import { useHat } from "../../context/HatContext";
import { useKnowledgeBase } from "../../context/KnowledgeBaseContext";
import { useWorkflow } from "../../context/WorkflowContext";
import { useSession } from "../../context/SessionContext";
import { useUser } from "../../context/UserContext";
import HatIcon from "../../icons/hat";
import HatBox from "../Model/HatBox";
import KnowledgeBaseBox from "../Model/KnowledgeBaseBox";
import WorkflowBox from "../Model/WorkflowBox";
import FeedbackModal from "../Layout/FeedbackModal";
import { useNotification } from "../../context/NotificationContext";
import { formatDistanceToNow } from 'date-fns';
import { useFeedback } from "../../context/FeedbackContext";
import { detectError } from "../../helpers/feedbackHelper";
import Tooltip from "../Tooltip/Tooltip";

export type AuthMode = "teams" | "cognito" | "none";

type NavbarProps = {
  messages: Message[];
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  isExpanded: boolean;
};

interface CombinedNotification {
  id: string;
  type: 'file' | 'va_shared' | 'meeting_transcription';
  title: string;
  message: string;
  status?: 'unread' | 'read' | string;
  created_at?: string;
  action_url?: string;
  va_name?: string;
  file_name?: string;
  shared_by_user_id?: string;
  sessionId?: string;
}

const Navbar: React.FC<NavbarProps> = ({
  messages,
  setIsExpanded,
  isExpanded,
}) => {
  const iconDefaultSize = "20";
  const auth = cognitoUseAuth();
  const { authMode } = useAuth();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { selectedHat, hatsList, setSelectedHat } = useHat();
  const {
    selectedKnowledgeBase,
    knowledgeBasesList,
    fetchKnowledgeBasesList,
    setSelectedKnowledgeBase,
    pinnedKnowledgeBaseId,
  } = useKnowledgeBase();

  // A BrainBank is fixed for the length of a conversation, the way the model is
  // (ModelListBox disables itself once there is a session). Every message posts
  // the BrainBank selected at that moment, so swapping one in halfway would
  // answer later turns off a different index than the earlier ones. Like the VA,
  // it can still be picked up mid-chat while the conversation has none, so this
  // keys off the BrainBank the conversation pinned on its first use.
  const isKnowledgeBaseLocked = Boolean(pinnedKnowledgeBaseId);

  // A VA is fixed for the rest of a conversation only once a BrainBank is in
  // use in it; until then the user can switch VAs mid-chat. It can also be
  // picked up mid-chat: a conversation started with only a BrainBank has no VA
  // until the user chooses one. So this keys off the VA the backend pinned to
  // the session plus the pinned BrainBank, not off the message count.
  const { sessionId, sessions } = useSession();
  const isHatLocked = Boolean(
    sessions.find((session) => session.id === sessionId)?.hat_id &&
      pinnedKnowledgeBaseId
  );
  const { selectedWorkflow, workflows, setSelectedWorkflow } = useWorkflow();
  const { userDetails, setUserDetails } = useUser();
  const {
    notifications: vaNotifications,
    unreadCount: vaUnreadCount,
    isLoadingNotifications,
    fetchNotifications,
    deleteNotificationById,
  } = useNotification();
  const [meetingNotifications, setMeetingNotifications] = useState<MeetingNotificationItem[]>([]);
  const [hatName, setHatName] = useState<string | null>(null);
  const [workflowName, setWorkflowName] = useState<string | null>(null);
  const [knowledgeBaseName, setKnowledgeBaseName] = useState<string | null>(null);
  const [isUpdatingMemory, setIsUpdatingMemory] = useState(false);
  const [isUpdatingShortTermMemory, setIsUpdatingShortTermMemory] = useState(false);
  const { openFeedbackModal } = useFeedback();

  // Calculate total notification count
  const totalNotificationCount = notificationCount + vaNotifications.length + meetingNotifications.length;

  // Create combined notifications array
  const combineNotifications = (): CombinedNotification[] => {
    const fileNotifications: CombinedNotification[] = files.map(file => ({
      id: `file_${file.id}`,
      type: 'file',
      title: file.file_name,
      message: `File upload status: ${file.status === "processed" ? "Processed" : file.status === "processing" ? "Processing" : file.status === "failed" ? "Failed" : "In Queue"}`,
      status: file.status || 'not_processed',
      file_name: file.file_name
    }));

    const vaNotificationsFormatted: CombinedNotification[] = vaNotifications.map(notif => ({
      id: notif.id,
      type: 'va_shared',
      title: notif.title,
      message: notif.message,
      status: notif.status,
      created_at: notif.created_at,
      action_url: notif.action_url,
      va_name: notif.va_name,
      shared_by_user_id: notif.shared_by_user_id
    }));

    const meetingNotificationsFormatted: CombinedNotification[] = meetingNotifications.map(notif => ({
      id: `meeting_${notif.sessionId}`,
      type: 'meeting_transcription' as const,
      title: notif.title,
      message: notif.status === 'completed'
        ? 'Transcription is ready!'
        : notif.status === 'failed'
          ? 'Transcription failed'
          : notif.status === 'transcribing'
            ? 'Transcribing audio...'
            : 'Processing recording...',
      status: notif.status,
      created_at: notif.created_at,
      sessionId: notif.sessionId,
    }));

    return [...meetingNotificationsFormatted, ...vaNotificationsFormatted, ...fileNotifications].sort((a, b) => {
      const aTime = a.created_at ? new Date(a.created_at).getTime() : Date.now();
      const bTime = b.created_at ? new Date(b.created_at).getTime() : Date.now();
      return bTime - aTime;
    });
  };

  // Handler for VA notification actions
  const handleVANotificationClick = async (notification: CombinedNotification) => {
    console.log('VA notification clicked:', notification);
    // No navigation - just closes the dropdown
  };

  // Handler for deleting VA notifications
  const handleDeleteVANotification = async (notification: CombinedNotification, e: React.MouseEvent) => {
    e.stopPropagation();
    const originalNotification = vaNotifications.find(n => n.id === notification.id);
    if (originalNotification) {
      await deleteNotificationById(notification.id, originalNotification.created_at);
    }
  };

  // Handler for deleting a single file notification
  const handleDeleteFileNotification = (notification: CombinedNotification) => {
    // File notification ids are prefixed with "file_" (see combineNotifications)
    const fileId = notification.id.replace(/^file_/, "");
    const updatedFiles = removeFileFromKnowledgeBase(fileId);
    setFiles(updatedFiles);
    setNotificationCount(updatedFiles.length);
    window.dispatchEvent(new CustomEvent("knowledgeBaseStorageUpdate"));
  };

  const combinedNotifications = combineNotifications();

  useEffect(() => {
    if (selectedHat) {
      setHatName(selectedHat.hat_name);
    } else {
      setHatName(null);
    }
  }, [selectedHat]);

  useEffect(() => {
    if (selectedWorkflow) {
      setWorkflowName(selectedWorkflow.workflow_name);
    } else {
      setWorkflowName(null);
    }
  }, [selectedWorkflow]);

  useEffect(() => {
    if (selectedKnowledgeBase) {
      setKnowledgeBaseName(selectedKnowledgeBase.name || null);
    } else {
      setKnowledgeBaseName(null);
    }
  }, [selectedKnowledgeBase]);

  useEffect(() => {
    const updateFilesAndCount = () => {
      const storedFiles = getFilesFromStorage();

      if (JSON.stringify(storedFiles) !== JSON.stringify(files)) {
        setFiles(storedFiles);

        const totalCount = storedFiles.length;
        setNotificationCount(totalCount);

        console.log("Updated files:", storedFiles);
        console.log("Updated notification count:", totalCount);
      }
    };

    updateFilesAndCount();

    const handleCustomEvent = () => {
      console.log("Custom event fired: knowledgeBaseStorageUpdate");
      updateFilesAndCount();
    };
    window.addEventListener("knowledgeBaseStorageUpdate", handleCustomEvent);

    const handleStorageChange = (e: StorageEvent) => {
      console.log("Storage event fired for key:", e.key);
      if (e.key === FILE_STORAGE_KEY) {
        updateFilesAndCount();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    const intervalId = setInterval(() => {
      console.log("Polling for notification updates");
      updateFilesAndCount();
    }, 5000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "knowledgeBaseStorageUpdate",
        handleCustomEvent
      );
      clearInterval(intervalId);
    };
  }, [files]);

  // Track meeting transcription notifications
  useEffect(() => {
    const updateMeetingNotifs = () => {
      const stored = getMeetingNotifications();
      setMeetingNotifications(stored);
    };

    updateMeetingNotifs();

    const handleMeetingUpdate = () => updateMeetingNotifs();
    window.addEventListener('meetingNotificationUpdate', handleMeetingUpdate);

    // Poll every 5 seconds to sync with MeetingContext's background polling
    const intervalId = setInterval(updateMeetingNotifs, 5000);

    return () => {
      window.removeEventListener('meetingNotificationUpdate', handleMeetingUpdate);
      clearInterval(intervalId);
    };
  }, []);

  const clearAllNotifications = async () => {
    try {
      // Clear file notifications (existing functionality)
      localStorage.removeItem(FILE_STORAGE_KEY);
      setFiles([]);
      setNotificationCount(0);
      window.dispatchEvent(new CustomEvent("knowledgeBaseStorageUpdate"));

      // Delete ALL VA notifications (instead of just marking as read)
      for (const notification of vaNotifications) {
        await deleteNotificationById(notification.id, notification.created_at);
      }

      // Clear meeting transcription notifications
      clearAllMeetingNotifications();
      setMeetingNotifications([]);

      toast.success("All notifications cleared");
    } catch (error) {
      console.error("Error clearing notifications:", error);
      toast.error("Failed to clear notifications");
    }
  };

  const handleHatChange = (hatId: string) => {
    // removing VA (when empty string is passed)
    if (!hatId || hatId === '') {
      setSelectedHat(null);
      toast.success('Virtual Assistant removed');
      return;
    }

    // selecting a new VA - deselect workflow (mutual exclusivity)
    const newHat = hatsList.find(hat => hat.id === hatId);
    if (newHat) {
      setSelectedHat(newHat);
      // Deselect workflow when VA is selected
      if (selectedWorkflow) {
        setSelectedWorkflow(null);
      }
      toast.success(`Switched to Virtual Assistant: ${newHat.hat_name}`);
    }
  };

  const handleKnowledgeBaseChange = (knowledgeBaseId: string) => {
    // removing BrainBank (when empty string is passed)
    if (!knowledgeBaseId || knowledgeBaseId === '') {
      setSelectedKnowledgeBase(null);
      toast.success('BrainBank removed');
      return;
    }

    // A BrainBank sits alongside a VA rather than replacing it: picking one
    // while a VA is selected swaps out that VA's own BrainBanks for this chat.
    const newKnowledgeBase = knowledgeBasesList.find(knowledgeBase => knowledgeBase.id === knowledgeBaseId);
    if (newKnowledgeBase) {
      setSelectedKnowledgeBase(newKnowledgeBase);
      toast.success(`Switched to BrainBank: ${newKnowledgeBase.name}`);
    }
  };

  const handleWorkflowChange = (workflowId: string) => {
    // removing Workflow (when empty string is passed)
    if (!workflowId || workflowId === '') {
      setSelectedWorkflow(null);
      toast.success('Workflow removed');
      return;
    }

    // selecting a new Workflow - deselect VA (mutual exclusivity)
    const newWorkflow = workflows.find(workflow => workflow.id === workflowId);
    if (newWorkflow) {
      setSelectedWorkflow(newWorkflow);
      // Deselect VA when workflow is selected
      if (selectedHat) {
        setSelectedHat(null);
      }
      // A workflow run never reads a BrainBank, so clear it too
      if (selectedKnowledgeBase) {
        setSelectedKnowledgeBase(null);
      }
      toast.success(`Switched to Workflow: ${newWorkflow.workflow_name}`);
    }
  };

  const handleNotification = async () => {
    console.log("handleNotification called");
    setIsLoading(true);
    setIsOpen(true);
    try {
      // Fetch VA notifications (new functionality)
      await fetchNotifications();

      // Handle file notifications (existing functionality)
      const storedFiles: FileItem[] = getFilesFromStorage();
      for (const storedFile of storedFiles) {
        const file = await getFile(storedFile.id);
        addFileToKnowledgeBase(file);
      }

      const updatedFiles = getFilesFromStorage();
      setFiles(updatedFiles);
      setNotificationCount(updatedFiles.length);

      toast.success("Notifications refreshed successfully");
    } catch (error) {
      console.log(error);
      toast.error("Error fetching notifications");
    } finally {
      setIsLoading(false);
    }
  };

  const requiredEnvVar = (name: string): string => {
    const value = process.env[name];
    if (!value) {
      throw new Error(`Missing required environment variable: ${name}`);
    }
    return value;
  };

  const signOutRedirect = () => {
    const clientId = requiredEnvVar("REACT_APP_COGNITO_CLIENT_ID");
    const logoutUri = requiredEnvVar("REACT_APP_REDIRECT_URI");
    const cognitoDomain = requiredEnvVar("REACT_APP_COGNITO_DOMAIN");
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
      logoutUri
    )}`;
  };

  const handleLogout = async () => {
    try {
      await auth.removeUser();
      toast.success("Logged out successfully");
      signOutRedirect();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleToggleLongTermMemory = async () => {
    if (!userDetails) return;

    setIsUpdatingMemory(true);
    try {
      const newValue = !userDetails.long_term_memory_enabled;
      await toggleLongTermMemory(newValue);

      setUserDetails({
        ...userDetails,
        long_term_memory_enabled: newValue
      });

      toast.success(`Long-term memory ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error("Error toggling long-term memory:", error);
      toast.error("Failed to update memory setting");
    } finally {
      setIsUpdatingMemory(false);
    }
  };

  const handleToggleShortTermMemory = async () => {
    if (!userDetails) return;

    setIsUpdatingShortTermMemory(true);
    try {
      const newValue = !userDetails.short_term_memory_enabled;
      await toggleShortTermMemory(newValue);

      setUserDetails({
        ...userDetails,
        short_term_memory_enabled: newValue
      });

      toast.success(`Short-term memory ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error("Error toggling short-term memory:", error);
      toast.error("Failed to update memory setting");
    } finally {
      setIsUpdatingShortTermMemory(false);
    }
  };

  const getStatusIcon = (status: string, type?: string) => {
    if (type === 'meeting_transcription') {
      switch (status) {
        case "completed":
          return (
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <Mic className="w-4 h-4 text-green-600" />
            </div>
          );
        case "failed":
          return (
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <Mic className="w-4 h-4 text-red-600" />
            </div>
          );
        default:
          return (
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center animate-pulse">
              <Mic className="w-4 h-4 text-blue-600" />
            </div>
          );
      }
    }
    switch (status) {
      case "processed":
        return <Check size={16} className="text-green-500" />;
      case "processing":
        return <Clock size={16} className="text-blue-500" />;
      case "failed":
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        // For "not_processed" or any other status, showing as "In Queue"
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  return (
    <div className="fixed z-40 top-0 left-0 flex flex-row items-center justify-between w-full py-3 md:py-4 text-sm bg-surface text-primary border-b border-transparent dark:border-border-default" style={{
      paddingLeft: window.innerWidth < 768 ? '1rem' : window.innerWidth < 1024 ? '224px' : '260px',
      paddingRight: window.innerWidth < 768 ? '1rem' : 'calc(2rem + 16px)',
      width: 'calc(100% - 16px)'
    }}>
      {/* Left side with menu */}
      <div className="flex flex-row items-center">
        <div className="sm:flex flex-row items-center justify-center space-x-2 pr-10 pl-3"></div>

        <div className="flex flex-row items-center justify-center space-x-2 pr-6 lg:pl-12">
          <Tooltip label="New chat" side="bottom">
            <a
              href="/"
              aria-label="New chat"
              className="flex items-center justify-center p-2 rounded-full text-muted hover:bg-surface-2 hover:text-primary transition-colors"
            >
              <SquarePen className="cursor-pointer" size={iconDefaultSize} />
            </a>
          </Tooltip>
        </div>

        <div className="lg:flex flex-col lg:flex-row items-start lg:items-center justify-center lg:space-x-2 pr-10">
          <ModelListBox />
          {/* Show WorkflowBox only if workflow is already selected */}
          {workflowName && (
            <WorkflowBox
              workflowName={workflowName}
              availableWorkflows={workflows}
              onWorkflowChange={handleWorkflowChange}
            />
          )}
          {/* Show HatBox if VA is selected OR if conversation exists without workflow */}
          {(hatName || (messages && messages.length > 0 && !workflowName)) && (
            <HatBox
              hatName={hatName}
              availableHats={hatsList}
              onHatChange={isHatLocked ? undefined : handleHatChange}
            />
          )}
          {/* Show KnowledgeBaseBox if a BrainBank is selected OR if a
              conversation exists without a workflow, the same rule as HatBox:
              this is where a BrainBank is picked once the chat has started.
              Withholding the change handler renders its locked, padlocked
              read-only state.

              Below lg the boxes in this row stack, and the navbar is fixed over
              the message list, so each extra one eats screen. On an empty chat
              the chat box carries its own BrainBank picker, making this one
              redundant there. */}
          {(knowledgeBaseName || (messages && messages.length > 0 && !workflowName)) && (
            <div className={messages && messages.length > 0 ? "" : "hidden lg:block"}>
              <KnowledgeBaseBox
                knowledgeBaseName={knowledgeBaseName}
                availableKnowledgeBases={knowledgeBasesList}
                onKnowledgeBaseChange={
                  isKnowledgeBaseLocked ? undefined : handleKnowledgeBaseChange
                }
                // Reload on open, so a BrainBank created during this chat is listed.
                onOpen={fetchKnowledgeBasesList}
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <div className="flex items-center justify-end">
          <div className="hidden md:flex items-center pr-3 relative"></div>
          <Listbox
            value={null}
            onChange={(value) => {
              if (value === "clear") {
                clearAllNotifications();
              }
            }}
          >
            <div className="relative">
              <Tooltip label="Notifications" side="bottom">
                <Listbox.Button
                  as="div"
                  className="flex items-center justify-center p-2 rounded-full text-muted hover:bg-surface-2 hover:text-primary transition-colors relative cursor-pointer"
                  onClick={() => {
                    handleNotification();
                    setIsOpen(!isOpen);
                  }}
                >
                  {isLoading ? (
                    <div className="animate-spin h-5 w-5 border-2 border-violet-700 border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <BellPlus
                        className="cursor-pointer"
                        size={iconDefaultSize}
                      />
                      {totalNotificationCount > 0 && (
                        <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                          {totalNotificationCount}
                        </div>
                      )}
                    </>
                  )}
                </Listbox.Button>
              </Tooltip>

              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setIsOpen(false)}
              >
                <Listbox.Options className="absolute mt-1 right-0 w-80 max-h-96 overflow-auto rounded-md bg-surface-overlay py-1 ring-1 ring-black/5 dark:ring-gray-700 focus:outline-none shadow-lg z-10">
                  {isLoading || isLoadingNotifications ? (
                    <div className="py-4 px-4 text-sm text-subtle text-center flex items-center justify-center">
                      <div className="animate-spin h-5 w-5 border-2 border-violet-700 border-t-transparent rounded-full mr-3" />
                      Loading notifications...
                    </div>
                  ) : combinedNotifications.length > 0 ? (
                    <>
                      <div className="sticky top-0 z-10 bg-surface-overlay border-b border-border-default flex justify-between items-center px-4 py-2">
                        <span className="text-sm font-medium text-muted">
                          Notifications ({combinedNotifications.length})
                        </span>
                        <button
                          onClick={clearAllNotifications}
                          className="flex items-center text-xs text-red-500 hover:text-red-700 cursor-pointer"
                        >
                          <X size={14} className="mr-1" />
                          Clear All
                        </button>
                      </div>
                      {combinedNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          className="relative cursor-pointer select-none py-3 px-4 hover:bg-surface-2 border-b border-border-default last:border-b-0"
                          onClick={() => {
                            if (notification.type === 'va_shared') {
                              handleVANotificationClick(notification);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start flex-1">
                              <div className="flex-shrink-0 mt-1 mr-3">
                                {notification.type === 'meeting_transcription' ? (
                                  getStatusIcon(notification.status || "", 'meeting_transcription')
                                ) : notification.type === 'file' ? (
                                  getStatusIcon(notification.status || "")
                                ) : (
                                  <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                                    <Building2 className="w-4 h-4 text-violet-600" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-medium text-primary truncate">
                                    {notification.title}
                                  </p></div>
                                <p className="text-xs text-muted mt-1">
                                  {notification.message}
                                </p>
                                {notification.type === 'va_shared' && notification.created_at && (
                                  <p className="text-xs text-subtle mt-1">
                                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                  </p>
                                )}
                                {notification.type === 'file' && (
                                  <p className="text-xs text-subtle mt-1">
                                    Status: {notification.status === "processed" ? "Processed" :
                                      notification.status === "processing" ? "Processing" :
                                        notification.status === "failed" ? "Failed" : "In Queue"}
                                  </p>
                                )}
                                {notification.type === 'meeting_transcription' && notification.created_at && (
                                  <p className="text-xs text-subtle mt-1">
                                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (notification.type === 'meeting_transcription' && notification.sessionId) {
                                  removeMeetingNotification(notification.sessionId);
                                  setMeetingNotifications(prev => prev.filter(n => n.sessionId !== notification.sessionId));
                                } else if (notification.type === 'file') {
                                  handleDeleteFileNotification(notification);
                                } else {
                                  handleDeleteVANotification(notification, e);
                                }
                              }}
                              className="flex-shrink-0 ml-2 p-1 hover:bg-red-100 rounded-full"
                              title="Clear notification"
                            >
                              <X size={14} className="text-gray-400 hover:text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="py-3 px-4 text-sm text-subtle text-center">
                      No notifications
                    </div>
                  )}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
        </div>

        <div className="hidden md:flex items-center pr-3 gap-2">
          <Listbox value={null} onChange={() => { }}>
            <div className="relative">
              <Tooltip label="Account" side="bottom">
                <Listbox.Button
                  aria-label="Account menu"
                  className="flex items-center justify-center p-2 rounded-full text-muted hover:bg-surface-2 hover:text-primary transition-colors"
                >
                  <User className="cursor-pointer" size={iconDefaultSize} />
                </Listbox.Button>
              </Tooltip>

              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute mt-1 right-0 w-52 overflow-auto rounded-md bg-surface-overlay py-1 ring-1 ring-black/5 dark:ring-gray-700 focus:outline-none sm:text-sm shadow-lg z-10">
                  <div
                    className="relative cursor-pointer select-none py-2 pl-10 pr-4 hover:bg-surface-2 text-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleToggleLongTermMemory();
                    }}
                  >
                    <div className="flex items-center justify-between cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Brain size={16} className="absolute left-3" />
                        <span className="block truncate">
                          Long-term Memory
                        </span>
                      </div>
                      <div className="flex items-center ml-2 mr-2">
                        {isUpdatingMemory ? (
                          <div className="animate-spin h-4 w-4 border-2 border-violet-700 border-t-transparent rounded-full" />
                        ) : (
                          <div
                            className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none ${userDetails?.long_term_memory_enabled
                              ? "bg-violet-600"
                              : "bg-gray-300"
                              }`}
                          >
                            <span
                              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${userDetails?.long_term_memory_enabled
                                ? "translate-x-4"
                                : "translate-x-0.5"
                                }`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className="relative cursor-pointer select-none py-2 pl-10 pr-4 hover:bg-surface-2 text-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleToggleShortTermMemory();
                    }}
                  >
                    <div className="flex items-center justify-between cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Brain size={16} className="absolute left-3" />
                        <span className="block truncate">
                          Short-term Memory
                        </span>
                      </div>
                      <div className="flex items-center ml-2 mr-2">
                        {isUpdatingShortTermMemory ? (
                          <div className="animate-spin h-4 w-4 border-2 border-violet-700 border-t-transparent rounded-full" />
                        ) : (
                          <div
                            className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none ${userDetails?.short_term_memory_enabled
                              ? "bg-violet-600"
                              : "bg-gray-300"
                              }`}
                          >
                            <span
                              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${userDetails?.short_term_memory_enabled
                                ? "translate-x-4"
                                : "translate-x-0.5"
                                }`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {authMode === "cognito" && (
                    <div
                      className="relative cursor-pointer select-none py-2 pl-10 pr-4 hover:bg-surface-2 text-primary"
                      onClick={handleLogout}
                    >
                      <div className="flex items-center gap-2 cursor-pointer">
                        <LogOut size={16} className="absolute left-3" />
                        <span className="block truncate">
                          Logout
                        </span>
                      </div>
                    </div>
                  )}
                </Listbox.Options>
              </Transition>
            </div>
          </Listbox>
          {/*feedback icon*/}
          <div className="hidden md:flex items-center pr-3">
            <Tooltip label="Send feedback" side="bottom">
              <button
                onClick={async () => {
                  const detected = detectError(messages);
                  if (detected) {
                    toast.info("Analyzing context to generate issue summary...");
                    try {
                      const conversationContext = messages.slice(-5).map(h => `${h.role}: ${typeof h.content === 'string' ? h.content : (h.content?.parts || "")}`).join('\n');
                      const summary = await generateFeedbackSummary(conversationContext);

                      if (summary && summary.subject && summary.description) {
                        openFeedbackModal({
                          initialSubject: summary.subject,
                          initialDescription: summary.description + "\n\nPlease check this issue."
                        });
                      } else {
                        openFeedbackModal({
                          initialSubject: `Error: ${detected.type} recorded`.slice(0, 50),
                          initialDescription: `I encountered an error.\n\nType: ${detected.type}${detected.tool ? `\nTool: ${detected.tool}` : ''}\nDetails: ${detected.message}`.slice(0, 500)
                        });
                      }
                    } catch(e) {
                      openFeedbackModal({
                        initialSubject: `Error: ${detected.type} recorded`.slice(0, 50),
                        initialDescription: `I encountered an error.\n\nType: ${detected.type}${detected.tool ? `\nTool: ${detected.tool}` : ''}\nDetails: ${detected.message}`.slice(0, 500)
                      });
                    }
                  } else {
                    openFeedbackModal();
                  }
                }}
                aria-label="Send feedback"
                className="flex items-center justify-center p-2 rounded-full text-muted hover:bg-surface-2 hover:text-primary transition-colors"
              >
                <Mail className="cursor-pointer" size={iconDefaultSize} />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;